import { createSlice } from "@reduxjs/toolkit";
import { Direction, Routes, routerChangeAction, TabName, UnitDisplay } from "const";
import { handleErrorStopLoading, handleFail, handleLoading, handleStopLoading } from "redux/redux-util";
import { STEPS } from "routes/SecondaryMarket/BuySell/Second-const";
import { mergeObj, concatCompact } from "utils/common-util";
import { calculate } from "utils/math";

const initialSetting = {
  unitDisplay: UnitDisplay.UNIT, // unit || baht
};

const initialState = {
  loading: false,
  secondMarketDetail: {},
  activeTabKey: TabName.SECOND_BUY_SELL,
  currPathname: null,
  productOfferingDetail: {},
  settlementDateList: [],
  sendFirstOTP: false,
  customerOutstanding: [],
  transactionType: null,
  customerOutstandingDetail: {},
};

const verifyReceiveAccountInitial = {
  verifying: false,
  isAccount: false,
  accountNo: null,
};

const initialDetail = {
  secondMarketDetail: {},
  verifyReceiveAccount: { ...verifyReceiveAccountInitial },
  stepPage: [STEPS.ProductDetail, Direction.Default],
  setting: { ...initialSetting },
  sendFirstOTP: false,
  transactionType: null,
  settlementDateList: [],
  productOfferingList: [],
  fetchConfirmationInfoDone: false,
  customerOutstandingDetail: {},
};

const isSecondaryList = (pathname) => pathname === Routes.Secondary.Products;

const isSecondaryListDetail = (pathname) =>
  !!pathname &&
  (pathname.startsWith("/secondary/product/") || pathname.startsWith("/secondary/securities/"));

const slice = createSlice({
  initialState,
  name: "secondMarket",
  extraReducers: (builder) => {
    builder.addCase(routerChangeAction, (state, { payload }) => {
      const pathname = payload.location.pathname;
      const prevPathname = state.currPathname;

      const leftListToNonDetail = isSecondaryList(prevPathname) && !isSecondaryListDetail(pathname);
      const leftDetailToNonList = isSecondaryListDetail(prevPathname) && !isSecondaryList(pathname);

      if (leftListToNonDetail || leftDetailToNonList) {
        state.activeTabKey = TabName.SECOND_BUY_SELL;
      }

      state.currPathname = pathname;
    });
  },
  reducers: {
    initSecondMarketDetail: (state, action) => {
      return {
        // ...initialState,
        ...state,
        ...initialDetail,
        secondMarketDetail: { ...action.payload },
      };
    },
    getProductOffering: handleLoading,
    getProductOfferingSuccess: (state, action) => {
      state.loading = false;
      state.productOfferingList = action.payload.productOfferingList;
    },
    getProductOfferingFailure: handleErrorStopLoading,

    getProductOfferingDetail: handleLoading,
    getProductOfferingDetailSuccess: (state, action) => {
      state.loading = false;
      state.productOfferingDetail = action.payload.productOffering;
      state.settlementDateList = action.payload.settlementDateList;
    },
    setProductOfferingDetailFromLombard: (state, action) => {
      state.productOfferingDetail = action.payload;
    },
    getProductOfferingDetailFailure: handleErrorStopLoading,
    updateProductOfferingRemainUnit: (state, { payload }) => {
      if (payload) {
        state.productOfferingDetail.remainingUnitAmt = payload;
        state.productOfferingDetail.remainingUnit = calculate(`${payload} / ${state.productOfferingDetail.initPar}`);
      }
    },
    updateProductOfferingMaxPerTxn: (state, { payload }) => {
      state.productOfferingDetail.maxUnitPerTxn = payload.maxUnitPerTxn;
      state.productOfferingDetail.maxAmtPerTxn = payload.maxAmtPerTxn;
    },

    onTabChange: (state, { payload }) => {
      state.activeTabKey = payload;
    },

    changeUnitDisplaySetting: (state, action) => {
      state.setting.unitDisplay = action.payload;
    },

    secondMarketDetailChange: (state, action) => {
      (() => {
        const { acceptDiscList = [], ...payload } = action.payload;
        // do not care falsy value, right process will must accepted all of this
        state.secondMarketDetail = mergeObj(state.secondMarketDetail, payload, {
          acceptDiscList: concatCompact(state.secondMarketDetail?.acceptDiscList, acceptDiscList),
        });
      })();
    },

    getConfirmationInfo: handleLoading,
    getConfirmationInfoSuccess: (state, action) => {
      state.loading = false;
      state.secondMarketDetail = mergeObj(state.secondMarketDetail, action.payload);
      state.fetchConfirmationInfoDone = true;
    },
    getConfirmationInfoFailure: handleErrorStopLoading,
    sendOTP: () => {},
    sendOTPSuccess: (state, action) => {
      state.secondMarketDetail.refOtp = action.payload;
      state.sendFirstOTP = true;
    },
    sendOTPFailure: handleFail,

    reSendOTP: () => {},
    reSendOTPSuccess: (state, action) => {
      state.secondMarketDetail.refOtp = action.payload;
    },
    reSendOTPFailure: handleFail,

    confirmPayment: handleLoading,
    confirmPaymentSuccess: (state, action) => {
      state.loading = false;
      state.secondMarketDetail = {
        ...state.secondMarketDetail,
        ...(action.payload ?? {}),
      };
    },
    confirmPaymentFailure: handleErrorStopLoading,
    verifyReceiveAccount: (state, { payload }) => {
      state.verifyReceiveAccount.verifying = true;
      state.verifyReceiveAccount.accountNo = payload.accountNo;
    },
    verifyReceiveAccountSuccess: (state, { payload }) => {
      state.verifyReceiveAccount.verifying = false;
      state.verifyReceiveAccount.isAccount = payload.isAccount;
    },
    verifyReceiveAccountFailure: (state, action) => {
      state.verifyReceiveAccount.verifying = false;
      state.verifyReceiveAccount.accountNo = null;
    },
    getCustomerOutstanding: handleLoading,
    getCustomerOutstandingSuccess: (state, action) => {
      state.loading = false;
      state.customerOutstanding = action.payload.securitiesList;
    },
    getCustomerOutstandingFailure: handleErrorStopLoading,

    getSecuritiesDetail: handleLoading,
    getSecuritiesDetailSuccess: (state, action) => {
      state.loading = false;
      state.customerOutstandingDetail = action.payload.security;
    },
    getSecuritiesDetailFailure: handleErrorStopLoading,

    stepChange: (state, { payload }) => {
      state.stepPage = payload;
    },

    fetchingProductDetailDone: (state) => {
      state.loading = false;
    },

    requestApproveLombard: handleLoading,
    requestApproveLombardSuccess: (state) => {
      state.loading = false;
      state.secondMarketDetail.exitWithoutPropmt = true;
    },
    requestApproveLombardFailure: handleErrorStopLoading,

    cancelLombard: handleLoading,
    cancelLombardSuccess: handleStopLoading,
    cancelLombardSuccessFailure: handleErrorStopLoading,

    completedPreTest: (state) => {
      state.stepPage = [STEPS.FactSheet, Direction.Default];
      state.secondMarketDetail.completedPreTest = true;
    },
  },
});

export const {
  getProductOffering,
  getProductOfferingSuccess,
  getProductOfferingFailure,
  getProductOfferingDetail,
  getProductOfferingDetailSuccess,
  getProductOfferingDetailFailure,
  updateProductOfferingRemainUnit,
  updateProductOfferingMaxPerTxn,
  onTabChange,
  initSecondMarketDetail,
  setProductOfferingDetailFromLombard,
  secondMarketDetailChange,

  getConfirmationInfo,
  getConfirmationInfoSuccess,
  getConfirmationInfoFailure,

  sendOTP,
  sendOTPSuccess,
  sendOTPFailure,
  reSendOTP,
  reSendOTPSuccess,
  reSendOTPFailure,

  confirmPayment,
  confirmPaymentSuccess,
  confirmPaymentFailure,

  verifyReceiveAccount,
  verifyReceiveAccountSuccess,
  verifyReceiveAccountFailure,

  getCustomerOutstanding,
  getCustomerOutstandingSuccess,
  getCustomerOutstandingFailure,

  getSecuritiesDetail,
  getSecuritiesDetailSuccess,
  getSecuritiesDetailFailure,

  stepChange,
  fetchingProductDetailDone,

  changeUnitDisplaySetting,

  requestApproveLombard,
  requestApproveLombardSuccess,
  requestApproveLombardFailure,

  cancelLombard,
  cancelLombardSuccess,
  cancelLombardFailure,

  completedPreTest,
} = slice.actions;

export default slice.reducer;
